/**
 * PC端文件工具函数
 * 提供文件上传、预览、下载等功能
 */

import { uploadFile, downloadFile, uploadChunk, mergeChunks, getChunkStatus } from '../api/file'
import { ElMessage, ElMessageBox } from 'element-plus'

// 允许的文件类型
const ALLOWED_FILE_TYPES = ['pdf', 'doc', 'docx', 'jpg', 'png', 'jpeg', 'xls', 'xlsx', 'txt']
// 禁止的文件类型（可执行文件和压缩包）
const FORBIDDEN_FILE_TYPES = ['exe', 'zip', 'rar', 'js', 'bat', 'cmd', 'sh', 'msi', 'dll', 'scr', 'com', 'pif', 'vbs', 'jar', 'app', 'deb', 'rpm']
// 文件大小限制
export const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
export const MAX_IMAGE_SIZE = 2 * 1024 * 1024 // 2MB
// 图片压缩目标大小
const SCAN_IMAGE_TARGET_SIZE = 500 * 1024 // 500KB（扫描件）
export const NORMAL_IMAGE_TARGET_SIZE = 250 * 1024 // 250KB（普通图片，目标200-300K，取中间值）
// 分片上传配置
const CHUNK_SIZE = 2 * 1024 * 1024 // 2MB per chunk
const CHUNK_UPLOAD_THRESHOLD = 5 * 1024 * 1024 // 超过5MB使用分片上传

// 下载缓存配置
const DOWNLOAD_CACHE_MAX_SIZE = 200 * 1024 * 1024 // 最大缓存200MB（提高大文件缓存支持）
const DOWNLOAD_CACHE_MAX_AGE = 30 * 24 * 60 * 60 * 1000 // 缓存30天
const DOWNLOAD_CACHE_FILE_SIZE_LIMIT = 50 * 1024 * 1024 // 单个文件最大50MB才缓存到内存
// 下载缓存：key为文件URL，value为 { blob, timestamp, fileName }
const downloadCache = new Map()
let downloadCacheTotalSize = 0

/**
 * 获取文件扩展名
 * @param {string} fileName 文件名
 * @returns {string} 扩展名（小写，不含点）
 */
function getFileExtension(fileName) {
  if (!fileName) return ''
  const parts = fileName.toLowerCase().split('.')
  return parts.length > 1 ? parts[parts.length - 1] : ''
}

/**
 * 判断是否为图片文件
 * @param {string} fileName 文件名
 * @returns {boolean}
 */
export function isImageFile(fileName) {
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
  return imageExts.includes(getFileExtension(fileName))
}

/**
 * 判断是否为扫描件（通常文件名包含"扫描"、"scan"等关键词，或尺寸较大）
 * @param {File} file 文件对象
 * @returns {Promise<boolean>}
 */
async function isScanImage(file) {
  if (!isImageFile(file.name)) return false
  
  // 检查文件名是否包含扫描相关关键词
  const fileName = file.name.toLowerCase()
  if (fileName.includes('扫描') || fileName.includes('scan') || fileName.includes('scanned')) {
    return true
  }
  
  // 检查图片尺寸，通常扫描件尺寸较大
  try {
    const dimensions = await getImageDimensions(file)
    // 如果宽度或高度超过2000px，可能是扫描件
    return dimensions.width > 2000 || dimensions.height > 2000
  } catch {
    return false
  }
}

/**
 * 获取图片尺寸
 * @param {File} file 图片文件
 * @returns {Promise<{width: number, height: number}>}
 */
function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.width, height: img.height })
    }
    
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('无法读取图片尺寸'))
    }
    
    img.src = url
  })
}

/**
 * 压缩图片
 * @param {File} file 原始图片文件
 * @param {number} targetSize 目标大小（字节）
 * @param {number} maxQuality 最大质量（0-1）
 * @param {number} minQuality 最小质量（0-1）
 * @returns {Promise<File>} 压缩后的文件
 */
async function compressImage(file, targetSize, maxQuality = 0.9, minQuality = 0.1) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    const originalSize = file.size
    
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height
        
        // 如果图片太大，先缩小尺寸（优化：根据目标大小动态调整最大尺寸）
        // 对于200-300K的目标，最大尺寸可以适当减小
        const maxDimension = targetSize <= 300 * 1024 ? 1920 : 2000
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height * maxDimension) / width
            width = maxDimension
          } else {
            width = (width * maxDimension) / height
            height = maxDimension
          }
        }
        
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        // 使用更好的图片渲染质量
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, width, height)
        
        // 强制转换为 JPEG 格式以获得更好的压缩率
        const outputType = 'image/jpeg'
        
        // 二分法查找合适的质量（优化：更精确的二分查找）
        let quality = maxQuality
        let minQ = minQuality
        let maxQ = maxQuality
        const maxIterations = 15 // 增加迭代次数，确保压缩到目标大小
        
        const tryCompress = (iteration = 0) => {
          if (iteration >= maxIterations) {
            // 达到最大迭代次数，使用当前质量创建文件
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('图片压缩失败'))
                return
              }
              // 确保压缩后的文件小于原文件，否则返回原文件
              if (blob.size >= originalSize) {
                console.warn(`[compressImage] 压缩后文件反而变大: ${formatFileSize(originalSize)} -> ${formatFileSize(blob.size)}, 返回原文件`)
                resolve(file)
                return
              }
              const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                type: outputType,
                lastModified: Date.now()
              })
              resolve(compressedFile)
            }, outputType, quality)
            return
          }
          
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('图片压缩失败'))
              return
            }
            
            // 如果压缩后反而变大，直接返回原文件
            if (blob.size >= originalSize) {
              console.warn(`[compressImage] 压缩后文件反而变大: ${formatFileSize(originalSize)} -> ${formatFileSize(blob.size)}, 返回原文件`)
              resolve(file)
              return
            }
            
            // 如果已经达到目标大小（允许5%的误差），创建新文件
            if (blob.size <= targetSize * 1.05 || (maxQ - minQ) < 0.01) {
              const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                type: outputType,
                lastModified: Date.now()
              })
              resolve(compressedFile)
            } else if (blob.size > targetSize) {
              // 文件太大，降低质量
              maxQ = quality
              quality = (quality + minQ) / 2
              tryCompress(iteration + 1)
            } else {
              // 文件太小，但不要提高质量超过目标大小（保持在目标大小的105%以内）
              if (blob.size < targetSize * 0.95) {
                minQ = quality
                quality = Math.min((quality + maxQ) / 2, maxQ)
                tryCompress(iteration + 1)
              } else {
                // 在目标大小的95%-105%之间，认为已经达到目标
                const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                  type: outputType,
                  lastModified: Date.now()
                })
                resolve(compressedFile)
              }
            }
          }, outputType, quality)
        }
        
        tryCompress()
      }
      
      img.onerror = () => {
        reject(new Error('图片加载失败'))
      }
      
      img.src = e.target.result
    }
    
    reader.onerror = () => {
      reject(new Error('文件读取失败'))
    }
    
    reader.readAsDataURL(file)
  })
}

/**
 * 压缩图片（支持多次压缩，确保达到目标大小）
 * @param {File} file 原始图片文件
 * @returns {Promise<File>} 压缩后的文件
 */
export async function compressImageFile(file) {
  if (!isImageFile(file.name)) {
    return file
  }
  
  const originalSize = file.size
  console.log(`[compressImageFile] 开始压缩: ${file.name}, 原始大小: ${formatFileSize(originalSize)}`)
  
  try {
    const isScan = await isScanImage(file)
    // 目标大小：扫描件500K，普通图片200-300K（使用250K作为目标）
    const targetSize = isScan ? SCAN_IMAGE_TARGET_SIZE : NORMAL_IMAGE_TARGET_SIZE
    const targetSizeText = isScan ? '500KB' : '250KB'
    console.log(`[compressImageFile] 目标大小: ${targetSizeText} (${formatFileSize(targetSize)}), 是否为扫描件: ${isScan}`)
    
    // 如果图片已经小于目标大小，不需要压缩
    if (file.size <= targetSize) {
      console.log(`[compressImageFile] 图片已经小于目标大小，不需要压缩`)
      return file
    }
    
    // 第一次压缩（使用较高的质量范围）
    console.log(`[compressImageFile] 第一次压缩，质量范围: 0.85-0.4`)
    let compressed = await compressImage(file, targetSize, 0.85, 0.4)
    console.log(`[compressImageFile] 第一次压缩完成: ${formatFileSize(compressed.size)}`)
    
    // 如果压缩后反而变大，直接返回原文件
    if (compressed.size >= originalSize) {
      console.warn(`[compressImageFile] 第一次压缩后文件反而变大，返回原文件`)
      return file
    }
    
    // 如果第一次压缩后仍然超过目标大小，进行二次压缩（使用较低的质量）
    if (compressed.size > targetSize) {
      console.log(`[compressImageFile] 第一次压缩后仍超过目标大小，进行二次压缩，质量范围: 0.5-0.2`)
      const secondCompressed = await compressImage(compressed, targetSize, 0.5, 0.2)
      console.log(`[compressImageFile] 第二次压缩完成: ${formatFileSize(secondCompressed.size)}`)
      
      // 如果第二次压缩后反而变大，使用第一次压缩的结果
      if (secondCompressed.size >= compressed.size) {
        console.warn(`[compressImageFile] 第二次压缩后文件反而变大，使用第一次压缩的结果`)
        // 保持 compressed 不变，不进行赋值
      } else {
        compressed = secondCompressed
      }
    }
    
    // 如果二次压缩后仍然超过目标大小，进行第三次压缩（更激进）
    // 扫描件：超过500K继续压缩；普通图片：超过300K继续压缩
    const maxSize = isScan ? SCAN_IMAGE_TARGET_SIZE : (300 * 1024)
    if (compressed.size > maxSize) {
      console.log(`[compressImageFile] 第二次压缩后仍超过最大限制 ${formatFileSize(maxSize)}，进行第三次压缩，质量范围: 0.3-0.1`)
      const thirdCompressed = await compressImage(compressed, targetSize, 0.3, 0.1)
      console.log(`[compressImageFile] 第三次压缩完成: ${formatFileSize(thirdCompressed.size)}`)
      
      // 如果第三次压缩后反而变大，使用之前的压缩结果
      if (thirdCompressed.size >= compressed.size) {
        console.warn(`[compressImageFile] 第三次压缩后文件反而变大，使用之前的压缩结果`)
      } else {
        compressed = thirdCompressed
      }
    }
    
    // 最终检查：确保压缩后的文件小于原文件
    if (compressed.size >= originalSize) {
      console.warn(`[compressImageFile] 压缩后文件反而变大: ${formatFileSize(originalSize)} -> ${formatFileSize(compressed.size)}, 返回原文件`)
      return file
    }
    
    console.log(`[compressImageFile] 压缩成功: ${formatFileSize(originalSize)} -> ${formatFileSize(compressed.size)}, 压缩率: ${((1 - compressed.size / originalSize) * 100).toFixed(1)}%`)
    return compressed
  } catch (error) {
    console.error('[compressImageFile] 图片压缩失败:', error)
    // 压缩失败时返回原文件
    return file
  }
}

/**
 * 校验文件类型
 * @param {File} file 文件对象
 * @returns {boolean} 是否允许
 */
export function validateFileType(file) {
  const ext = getFileExtension(file.name)
  
  // 检查是否在禁止列表中
  if (FORBIDDEN_FILE_TYPES.includes(ext)) {
    return false
  }
  
  // 检查是否在允许列表中
  return ALLOWED_FILE_TYPES.includes(ext)
}

/**
 * 格式化文件大小
 * @param {number} bytes 字节数
 * @returns {string}
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

/**
 * 生成唯一的上传ID
 * @returns {string}
 */
function generateUploadId() {
  return 'upload_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
}

/**
 * 分片上传文件（适用于大文件）
 * @param {File} file 文件对象
 * @param {string} businessType 业务类型
 * @param {string} module 模块名称
 * @param {Function} onProgress 上传进度回调
 * @returns {Promise<{url: string, fileName: string, size: number}>}
 */
async function uploadFileInChunks(file, businessType, module, onProgress) {
  // 生成唯一的上传ID
  const uploadId = generateUploadId()
  
  // 计算分片信息
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
  const fileName = file.name + '_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  const originalFileName = file.name
  
  // 1. 检查已上传分片（断点续传）
  let uploadedChunks = new Set()
  try {
    const statusRes = await getChunkStatus(uploadId)
    if (statusRes.code === '000000' && statusRes.data) {
      // 后端返回的是 Set<Integer>，转换为 Set
      if (Array.isArray(statusRes.data)) {
        uploadedChunks = new Set(statusRes.data)
      } else if (statusRes.data instanceof Set) {
        uploadedChunks = statusRes.data
      }
      if (uploadedChunks.size > 0) {
        console.log(`断点续传：uploadId ${uploadId} 已上传分片:`, Array.from(uploadedChunks))
      }
    }
  } catch (error) {
    console.warn('获取分片状态失败，从头开始上传:', error)
  }
  
  // 2. 上传所有分片
  const uploadPromises = []
  for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
    // 如果分片已存在，跳过
    if (uploadedChunks.has(chunkNumber)) {
      console.log(`分片 ${chunkNumber + 1}/${totalChunks} 已存在，跳过上传`)
      if (onProgress) {
        const progress = Math.round(((chunkNumber + 1) / totalChunks) * 90) // 90%用于上传，10%用于合并
        onProgress(progress)
      }
      continue
    }
    
    const start = chunkNumber * CHUNK_SIZE
    const end = Math.min(start + CHUNK_SIZE, file.size)
    const chunk = file.slice(start, end)
    const chunkSize = chunk.size
    
    // 上传分片的进度回调
    const chunkProgress = (progress) => {
      if (onProgress) {
        // 当前分片的进度 + 已完成分片的进度
        const chunkProgressPercent = progress / 100
        const completedChunks = uploadedChunks.size + chunkNumber
        const totalProgress = Math.round(((completedChunks + chunkProgressPercent) / totalChunks) * 90) // 90%用于上传
        onProgress(totalProgress)
      }
    }
    
    // 创建上传Promise
    const uploadPromise = uploadChunk(
      chunk,
      uploadId,
      chunkNumber,
      totalChunks,
      chunkSize,
      file.size,
      fileName,
      originalFileName,
      businessType,
      module,
      chunkProgress
    ).then(res => {
      if (res.code !== '000000') {
        throw new Error(res.msg || '分片上传失败')
      }
      // 标记分片已上传
      uploadedChunks.add(chunkNumber)
      return res
    }).catch(error => {
      console.error(`分片 ${chunkNumber + 1}/${totalChunks} 上传失败:`, error)
      throw new Error(`分片上传失败: ${error.message}`)
    })
    
    uploadPromises.push(uploadPromise)
  }
  
  // 等待所有分片上传完成
  await Promise.all(uploadPromises)
  
  // 3. 合并分片
  // 合并分片时，进度应该是 90-100%（90%用于上传，10%用于合并）
  // 为了更平滑，合并开始时设置为 90%，合并完成后设置为 100%
  if (onProgress) {
    onProgress(90) // 合并开始，进度 90%
  }
  
  try {
    const mergeRes = await mergeChunks(uploadId)
    
    if (mergeRes.code !== '000000' || !mergeRes.data) {
      throw new Error(mergeRes.msg || '分片合并失败')
    }
    
    // 合并完成，进度 100%
    if (onProgress) {
      onProgress(100)
    }
    
    return {
      url: mergeRes.data.url,
      fileName: mergeRes.data.fileName || originalFileName,
      size: mergeRes.data.size || file.size
    }
  } catch (error) {
    console.error('分片合并失败:', error)
    throw new Error(`分片合并失败: ${error.message}`)
  }
}

/**
 * 上传文件（PC端）
 * @param {File} file 文件对象
 * @param {string} businessType 业务类型
 * @param {string} module 模块名称
 * @param {Function} onProgress 上传进度回调
 * @returns {Promise<{url: string, fileName: string, size: number}>}
 */
export async function uploadFileToServer(file, businessType, module, onProgress) {
  try {
    console.log(`[uploadFileToServer] 开始上传文件: ${file.name}, 大小: ${formatFileSize(file.size)}, 是否为图片: ${isImageFile(file.name)}`)
    
    // 校验文件类型
    if (!validateFileType(file)) {
      const ext = getFileExtension(file.name)
      ElMessage.error(`不支持的文件类型：${ext}，请上传允许的文件格式`)
      throw new Error('不支持的文件类型')
    }
    
    // 检查文件大小
    let maxSize = MAX_FILE_SIZE
    let sizeLimitText = '20MB'
    
    if (isImageFile(file.name)) {
      maxSize = MAX_IMAGE_SIZE
      sizeLimitText = '2MB'
      console.log(`[uploadFileToServer] 图片文件，限制大小: ${formatFileSize(maxSize)}`)
      
      // 图片在上传前再次压缩（如果之前没有压缩过，或者压缩后仍然较大）
      // 注意：在 AttachmentUpload 组件中已经压缩过了，这里作为二次保障
      if (file.size > NORMAL_IMAGE_TARGET_SIZE) {
        console.log(`[uploadFileToServer] 图片大小 ${formatFileSize(file.size)} 大于目标大小 ${formatFileSize(NORMAL_IMAGE_TARGET_SIZE)}，尝试压缩`)
        if (onProgress) {
          onProgress(5)
        }
        
        try {
          const originalSize = file.size
          file = await compressImageFile(file)
          console.log(`[uploadFileToServer] 压缩完成: ${formatFileSize(originalSize)} -> ${formatFileSize(file.size)}`)
          if (onProgress) {
            onProgress(20)
          }
        } catch (error) {
          console.warn('[uploadFileToServer] 图片压缩失败，使用原文件:', error)
        }
      } else {
        console.log(`[uploadFileToServer] 图片大小 ${formatFileSize(file.size)} 小于目标大小 ${formatFileSize(NORMAL_IMAGE_TARGET_SIZE)}，不需要压缩`)
      }
    }
    
    console.log(`[uploadFileToServer] 最终文件大小: ${formatFileSize(file.size)}, 限制: ${formatFileSize(maxSize)}`)
    if (file.size > maxSize) {
      console.error(`[uploadFileToServer] 文件大小超过限制！文件: ${file.name}, 大小: ${formatFileSize(file.size)}, 限制: ${formatFileSize(maxSize)}`)
      ElMessage.error(`${isImageFile(file.name) ? '图片' : '文件'}大小不能超过${sizeLimitText}，当前大小：${formatFileSize(file.size)}`)
      throw new Error('文件大小超过限制')
    }
    
    console.log(`[uploadFileToServer] 文件大小检查通过: ${file.name}`)

    // 对于大文件（超过阈值），使用优化的上传方式
    const useChunkUpload = file.size > CHUNK_UPLOAD_THRESHOLD
    
    if (useChunkUpload) {
      // 使用优化的上传方式（提供更精确的进度反馈）
      // uploadFileInChunks 已经返回 0-100 的进度，直接传递即可
      // 注意：uploadFileInChunks 内部已经会在完成时调用 onProgress(100)，这里不需要再调用
      const result = await uploadFileInChunks(file, businessType, module, (progress) => {
        if (onProgress) {
          // progress 已经是 0-100，直接传递
          onProgress(Math.min(progress, 100))
        }
      })
      
      // uploadFileInChunks 内部已经调用了 onProgress(100)，这里不需要再调用
      return result
    } else {
      // 小文件使用常规上传
      const res = await uploadFile(file, businessType, module, (progress) => {
        if (onProgress) {
          // progress 已经是 0-100，直接传递
          onProgress(Math.min(progress, 100))
        }
      })
      
      if (res.code === '000000' && res.data) {
        if (onProgress) {
          onProgress(100)
        }
        return {
          url: res.data.url,
          fileName: res.data.fileName || file.name,
          size: res.data.size || file.size
        }
      } else {
        throw new Error(res.msg || '上传失败')
      }
    }
  } catch (error) {
    console.error('文件上传失败:', error)
    if (error.message !== '不支持的文件类型' && error.message !== '文件大小超过限制') {
      ElMessage.error(error.message || '文件上传失败')
    }
    throw error
  }
}

/**
 * 预览图片
 * @param {string} url 图片URL
 */
export function previewImage(url) {
  if (!url) {
    ElMessage.warning('图片URL为空')
    return
  }
  
  // 使用Element Plus的图片预览组件
  // 这里可以集成图片预览功能
  window.open(url, '_blank')
}

/**
 * 清理下载缓存（移除过期或超出大小的缓存）
 */
function cleanDownloadCache() {
  const now = Date.now()
  const keysToDelete = []
  
  // 清理过期缓存
  for (const [key, value] of downloadCache.entries()) {
    if (now - value.timestamp > DOWNLOAD_CACHE_MAX_AGE) {
      keysToDelete.push(key)
      downloadCacheTotalSize -= value.blob.size
    }
  }
  
  // 如果缓存仍然太大，按时间顺序删除最旧的
  if (downloadCacheTotalSize > DOWNLOAD_CACHE_MAX_SIZE) {
    const sortedEntries = Array.from(downloadCache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp)
    
    for (const [key, value] of sortedEntries) {
      if (downloadCacheTotalSize <= DOWNLOAD_CACHE_MAX_SIZE) break
      if (!keysToDelete.includes(key)) {
        keysToDelete.push(key)
        downloadCacheTotalSize -= value.blob.size
      }
    }
  }
  
  // 删除标记的键
  keysToDelete.forEach(key => {
    downloadCache.delete(key)
  })
}

/**
 * 下载文件（PC端）
 * @param {string} url 文件URL
 * @param {string} fileName 文件名（可选）
 */
export async function downloadFileFromServer(url, fileName) {
  try {
    if (!url) {
      ElMessage.warning('文件URL为空')
      return
    }

    // 清理过期缓存
    cleanDownloadCache()
    
    // 生成缓存key（只使用URL，不包含fileName，因为相同URL应该使用相同缓存）
    const cacheKey = url
    const cached = downloadCache.get(cacheKey)
    const now = Date.now()
    
    if (cached && (now - cached.timestamp) < DOWNLOAD_CACHE_MAX_AGE) {
      // 使用内存缓存
      console.log('✅ 使用内存缓存下载文件:', url, `(${(cached.blob.size / 1024 / 1024).toFixed(2)}MB)`)
      const blobUrl = window.URL.createObjectURL(cached.blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = cached.fileName || fileName || 'file'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
      ElMessage.success('文件下载成功')
      return
    }

    // 对于大文件，浏览器会自动使用HTTP缓存（后端已设置Cache-Control: public, max-age=2592000）
    // 如果浏览器缓存中有，会直接使用，不会发起网络请求
    console.log('📥 从服务器下载文件:', url, fileName ? `(${fileName})` : '')
    ElMessage.info('开始下载文件...')
    
    const response = await downloadFile(url, fileName)
    
    // 从响应头获取文件名，或使用传入的文件名
    let downloadFileName = fileName
    if (!downloadFileName && response.headers) {
      const contentDisposition = response.headers['content-disposition']
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (fileNameMatch && fileNameMatch[1]) {
          downloadFileName = fileNameMatch[1].replace(/['"]/g, '')
          // 处理URL编码的文件名
          try {
            downloadFileName = decodeURIComponent(downloadFileName)
          } catch (e) {
            console.warn('解码文件名失败:', e)
          }
        }
      }
    }
    
    if (!downloadFileName) {
      // 从URL中提取文件名
      const urlParts = url.split('/')
      downloadFileName = urlParts[urlParts.length - 1] || 'file'
    }

    // 创建blob并缓存
    const blob = new Blob([response])
    
    // 缓存文件（缓存小于50MB的文件到内存，大文件依赖浏览器HTTP缓存）
    if (blob.size < DOWNLOAD_CACHE_FILE_SIZE_LIMIT) {
      downloadCache.set(cacheKey, {
        blob: blob,
        timestamp: now,
        fileName: downloadFileName
      })
      downloadCacheTotalSize += blob.size
      console.log(`文件已缓存到内存: ${downloadFileName} (${(blob.size / 1024 / 1024).toFixed(2)}MB})`)
    } else {
      console.log(`文件过大，使用浏览器HTTP缓存: ${downloadFileName} (${(blob.size / 1024 / 1024).toFixed(2)}MB)`)
    }

    // 下载文件
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = downloadFileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
    
    ElMessage.success('文件下载成功')
  } catch (error) {
    console.error('文件下载失败:', error)
    ElMessage.error(error.message || '文件下载失败')
  }
}

/**
 * 选择文件（PC端）
 * @param {Object} options 配置选项
 * @param {string} options.accept 接受的文件类型（如：'image/*', '.pdf,.doc,.docx'）
 * @param {boolean} options.multiple 是否多选
 * @param {boolean} options.isImage 是否为图片选择
 * @returns {Promise<FileList>}
 */
export function chooseFile(options = {}) {
  const {
    accept = '*/*',
    multiple = false,
    isImage = false // eslint-disable-line no-unused-vars
  } = options

  return new Promise((resolve, reject) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.multiple = multiple
    
    input.onchange = (e) => {
      const files = e.target.files
      if (!files || files.length === 0) {
        reject(new Error('未选择文件'))
        return
      }

      // 校验文件类型和大小（图片在选择时立即检测）
      const invalidFiles = []
      const validFiles = []
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        // 检查文件类型
        if (!validateFileType(file)) {
          const ext = getFileExtension(file.name)
          invalidFiles.push({
            name: file.name,
            reason: `不支持的文件类型：${ext}`
          })
          continue
        }
        
        // 图片文件：无论 isImage 参数如何，都使用2MB限制
        // 必须严格检查，超过2MB直接拒绝
        if (isImageFile(file.name)) {
          console.log(`[chooseFile] 检查图片: ${file.name}, 大小: ${file.size} 字节 (${formatFileSize(file.size)}), 限制: ${MAX_IMAGE_SIZE} 字节 (${formatFileSize(MAX_IMAGE_SIZE)})`)
          // 严格检查：超过2MB（2097152字节）直接拒绝
          if (file.size > MAX_IMAGE_SIZE) {
            console.warn(`[chooseFile] 图片 ${file.name} 超过2MB限制，拒绝`)
            invalidFiles.push({
              name: file.name,
              reason: `图片大小不能超过2MB，当前大小：${formatFileSize(file.size)}`
            })
            continue
          }
          console.log(`[chooseFile] 图片 ${file.name} 大小符合要求`)
          // 图片文件且大小符合要求，添加到有效文件列表
          validFiles.push(file)
        } else {
          // 非图片文件：检查文件大小，使用20MB限制
          if (file.size > MAX_FILE_SIZE) {
            invalidFiles.push({
              name: file.name,
              reason: `文件大小超过20MB限制（当前：${formatFileSize(file.size)}）`
            })
            continue
          }
          // 非图片文件且大小符合要求，添加到有效文件列表
          validFiles.push(file)
        }
      }

      console.log(`[chooseFile] 文件校验完成，有效文件数: ${validFiles.length}, 无效文件数: ${invalidFiles.length}`)

      // 如果没有有效文件，拒绝
      if (validFiles.length === 0) {
        if (invalidFiles.length > 0) {
          const fileList = invalidFiles.map(f => `${f.name} - ${f.reason}`).join('\n')
          ElMessage.error(`以下文件不符合要求：\n${fileList}`)
          reject(new Error('文件校验失败'))
        } else {
          reject(new Error('未选择有效文件'))
        }
        return
      }

      // 创建一个新的 FileList 对象，只包含有效文件
      const dataTransfer = new DataTransfer()
      validFiles.forEach(file => {
        dataTransfer.items.add(file)
      })
      
      // 返回文件列表和无效文件信息（供调用者处理）
      const result = {
        files: dataTransfer.files,
        invalidFiles: invalidFiles // 返回无效文件信息，供调用者显示提示
      }
      resolve(result)
    }
    
    input.oncancel = () => {
      reject(new Error('用户取消选择'))
    }
    
    input.click()
  })
}

/**
 * 选择图片文件
 * @param {boolean} multiple 是否多选
 * @returns {Promise<FileList>}
 */
export function chooseImage(multiple = false) {
  return chooseFile({
    accept: 'image/jpeg,image/png,image/jpg',
    multiple,
    isImage: true
  })
}

/**
 * 选择文档文件
 * @param {boolean} multiple 是否多选
 * @returns {Promise<FileList>}
 */
export function chooseDocument(multiple = false) {
  return chooseFile({
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.txt',
    multiple,
    isImage: false
  })
}

/**
 * 选择附件（支持图片和文件）
 * @param {boolean} multiple 是否多选
 * @returns {Promise<FileList>}
 */
export function chooseAttachment(multiple = false) {
  return chooseFile({
    accept: 'image/jpeg,image/png,image/jpg,image/gif,image/bmp,image/webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.ppt,.pptx',
    multiple,
    isImage: false // 这里设为 false，因为会混合选择
  })
}

/**
 * 检查文件是否重复
 * @param {File} file 文件对象
 * @param {Array} existingFiles 已存在的文件列表（可以是字符串数组或对象数组）
 * @returns {Promise<{isDuplicate: boolean, duplicateCount: number, shouldContinue: boolean}>}
 */
export async function checkDuplicateFile(file, existingFiles) {
  if (!file || !existingFiles || existingFiles.length === 0) {
    return { isDuplicate: false, duplicateCount: 0, shouldContinue: true }
  }
  
  const fileName = file.name.toLowerCase()
  let duplicateCount = 0
  
  // 统计重复文件数量
  for (const existingFile of existingFiles) {
    let existingFileName = ''
    if (typeof existingFile === 'string') {
      // 如果是URL字符串，提取文件名
      const urlParts = existingFile.split('/')
      existingFileName = urlParts[urlParts.length - 1].toLowerCase()
    } else if (existingFile.fileName) {
      existingFileName = existingFile.fileName.toLowerCase()
    } else if (existingFile.name) {
      existingFileName = existingFile.name.toLowerCase()
    } else if (existingFile.file && existingFile.file.name) {
      // 待上传的文件对象
      existingFileName = existingFile.file.name.toLowerCase()
    }
    
    if (existingFileName === fileName) {
      duplicateCount++
    }
  }
  
  if (duplicateCount === 0) {
    return { isDuplicate: false, duplicateCount: 0, shouldContinue: true }
  }
  
  // 如果重复次数达到2次，拒绝上传
  if (duplicateCount >= 2) {
    await ElMessageBox.alert('系统检测到非法上传，本次文件无法上传，请重新选择其他文件。', '提示', {
      type: 'error',
      confirmButtonText: '知道了'
    })
    return { isDuplicate: true, duplicateCount, shouldContinue: false }
  }
  
  // 如果重复次数为1或2，询问用户是否继续
  if (duplicateCount >= 1) {
    try {
      await ElMessageBox.confirm('您上传的附件重复，确认继续上传吗？', '提示', {
        type: 'warning',
        confirmButtonText: '继续上传',
        cancelButtonText: '取消'
      })
      return { isDuplicate: true, duplicateCount, shouldContinue: true }
    } catch {
      return { isDuplicate: true, duplicateCount, shouldContinue: false }
    }
  }
  
  return { isDuplicate: false, duplicateCount: 0, shouldContinue: true }
}

/**
 * 批量上传文件（在提交表单时调用）
 * @param {Array} files 文件对象数组，格式：[{file: File, fileName: string, size: number, ...}]
 * @param {string} businessType 业务类型
 * @param {string} module 模块名称
 * @param {Function} onProgress 总进度回调 (totalProgress: number) => {}
 * @returns {Promise<Array>} 上传结果数组，格式：[{url: string, fileName: string, size: number}]
 */
export async function batchUploadFiles(files, businessType, module, onProgress) {
  if (!files || files.length === 0) {
    return []
  }
  
  const results = []
  const totalFiles = files.length
  // 跟踪当前最大进度，确保进度不会回退
  let maxProgress = 0
  
  // 进度更新函数，确保进度单调递增
  const updateProgress = (progress) => {
    if (onProgress) {
      const clampedProgress = Math.min(Math.max(progress, 0), 100) // 限制在 0-100 范围内
      if (clampedProgress > maxProgress) {
        maxProgress = clampedProgress
        onProgress(maxProgress)
      }
    }
  }
  
  for (let i = 0; i < files.length; i++) {
    const fileItem = files[i]
    const file = fileItem.file || fileItem
    
    try {
      // 如果已经有url，说明已经上传过，直接使用
      if (fileItem.url && !fileItem.file) {
        results.push({
          url: fileItem.url,
          fileName: fileItem.fileName || fileItem.name || file.name,
          originalFileName: fileItem.originalFileName || fileItem.fileName || fileItem.name || file.name,
          size: fileItem.size || file.size
        })
        // 已完成文件，进度 = (已完成文件数 / 总文件数) * 100
        const completedProgress = ((i + 1) / totalFiles) * 100
        updateProgress(completedProgress)
        continue
      }
      
      // 上传文件
      const result = await uploadFileToServer(
        file,
        businessType,
        module,
        (progress) => {
          // 当前文件进度 + 已完成文件进度
          // progress 是 0-100 的百分比
          const currentFileProgress = Math.min(Math.max(progress, 0), 100) / 100 // 确保在 0-1 范围内
          const completedProgress = i / totalFiles // 已完成文件数 / 总文件数
          const totalProgress = (completedProgress + currentFileProgress / totalFiles) * 100
          updateProgress(totalProgress)
        }
      )
      
      // 文件上传完成，确保进度更新到正确位置
      // 已完成文件数 = i + 1，进度 = (已完成文件数 / 总文件数) * 100
      const completedProgress = ((i + 1) / totalFiles) * 100
      updateProgress(completedProgress)
      
      // 保存原始文件名（后端返回的 fileName 是原始文件名）
      const originalFileName = fileItem.originalFileName || fileItem.fileName || file.name
      results.push({
        url: result.url,
        fileName: result.fileName || file.name, // 后端返回的原始文件名
        originalFileName: result.fileName || originalFileName, // 优先使用后端返回的原始文件名
        size: result.size || file.size
      })
    } catch (error) {
      console.error(`上传文件失败: ${file.name}`, error)
      throw new Error(`文件 "${file.name}" 上传失败: ${error.message}`)
    }
  }
  
  // 所有文件上传完成，确保进度是 100%
  updateProgress(100)
  
  return results
}

/**
 * 上传限流管理器
 * 用于控制文件上传频率，防止频繁上传
 */
class UploadRateLimiter {
  constructor(options = {}) {
    // 默认配置：1分钟内最多上传5次
    this.rateLimit = options.rateLimit || 5 // 时间窗口内最多上传次数
    this.rateWindow = options.rateWindow || 60 * 1000 // 时间窗口（毫秒），默认1分钟
    this.lastUploadTime = 0 // 上次上传时间
    this.uploadCount = 0 // 当前时间窗口内的上传次数
  }

  /**
   * 检查是否可以上传
   * @returns {boolean} true表示可以上传，false表示被限流
   */
  check() {
    const now = Date.now()
    
    // 如果超过时间窗口，重置计数
    if (now - this.lastUploadTime > this.rateWindow) {
      this.uploadCount = 0
      this.lastUploadTime = now
    }
    
    // 检查是否超过限流
    if (this.uploadCount >= this.rateLimit) {
      return false
    }
    
    return true
  }

  /**
   * 记录一次上传（上传成功后调用）
   */
  record() {
    this.uploadCount++
    this.lastUploadTime = Date.now()
  }

  /**
   * 重置限流状态
   */
  reset() {
    this.uploadCount = 0
    this.lastUploadTime = 0
  }
}

// 创建全局的上传限流实例（默认配置）
const defaultRateLimiter = new UploadRateLimiter()

/**
 * 检查上传限流（使用默认限流器）
 * @returns {boolean} true表示可以上传，false表示被限流
 */
export function checkUploadRateLimit() {
  return defaultRateLimiter.check()
}

/**
 * 记录上传（使用默认限流器）
 * 上传成功后调用此函数记录上传次数
 */
export function recordUpload() {
  defaultRateLimiter.record()
}

/**
 * 创建自定义的上传限流器
 * @param {Object} options 配置选项
 * @param {number} options.rateLimit 时间窗口内最多上传次数（默认5次）
 * @param {number} options.rateWindow 时间窗口（毫秒，默认60000即1分钟）
 * @returns {UploadRateLimiter} 限流器实例
 */
export function createUploadRateLimiter(options = {}) {
  return new UploadRateLimiter(options)
}

